import { Box, Theme } from '@mui/material';
import dayjs, { Dayjs } from "dayjs";
import { Todo } from "../../../types/types";
import { CalendarGrid as StyledCalendarGrid, WeekDayCell, WeekHeader } from '../styles/styles';
import CalendarDay from './CalendarDay/CalendarDay';
import { useState } from 'react';

export interface CalendarGridProps {
    weekDays: string[];
    calendarDays: Dayjs[];
    currentDate: Dayjs;
    selectedDate: Dayjs | null;
    getTodosForDate: (date: Dayjs) => Todo[];
    getTodoColor: (todo: Todo, theme: Theme) => string | undefined;
    handleTodoClick: (todo: Todo) => void;
    isAddMode: boolean;
    setAddMode: (addMode: boolean) => void;
    onDateClick: (date: Dayjs) => void;
    onSaveTodo: (date: Dayjs, text: string) => Promise<Todo | null> | undefined;
    showTime: boolean;
}

const sortTodosByDueTime = (todos: Todo[]): Todo[] => {
    return [...todos].sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;

        if (dayjs(a.dueDate).isSame(b.dueDate)) {
            if (a.completed === b.completed) return 0;
            return a.completed ? 1 : -1;
        }

        return dayjs(a.dueDate).isBefore(b.dueDate) ? -1 : 1;
    });
};
const CalendarGrid: React.FC<CalendarGridProps> = ({
    weekDays,
    calendarDays,
    currentDate,
    selectedDate,
    getTodosForDate,
    getTodoColor,
    handleTodoClick,
    isAddMode,
    setAddMode,
    onDateClick,
    onSaveTodo,
    showTime
}) => {
    const [editingDay, setEditingDay] = useState<Dayjs | null>(null);

    const getSortedTodosForDate = (date: Dayjs): Todo[] => {
        const todos = getTodosForDate(date);
        return sortTodosByDueTime(todos);
    };

    return (
        <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
        }}>
            <WeekHeader>
                {weekDays.map(day => (
                    <WeekDayCell key={day}>{day}</WeekDayCell>
                ))}
            </WeekHeader>

            <StyledCalendarGrid>
                {calendarDays.map((day, index) => {
                    const isCurrentMonth = currentDate.isSame(day, 'month');

                    return (
                        <CalendarDay
                            key={index}
                            day={day}
                            isCurrentMonth={isCurrentMonth}
                            isSelected={selectedDate?.isSame(day, 'day') || false}
                            onTodoClick={handleTodoClick}
                            getTodoColor={getTodoColor}
                            isAddMode={isAddMode}
                            setAddMode={setAddMode}
                            editingDay={editingDay}
                            setEditingDay={setEditingDay}
                            onAddClick={() => onDateClick(day)}
                            onSaveTodo={onSaveTodo}
                            getTodosForDate={getSortedTodosForDate}
                            showTime={showTime}
                        />
                    );
                })}
            </StyledCalendarGrid>
        </Box>
    );
};

export default CalendarGrid; 